import {
  accountMutationHeaders,
  browserAccountStateSessionRepository,
  readBoundedAccountJson,
} from "../account/account-state-session-repository.js";
import { createAccountOperationId } from "../account/account-operation-id.js";
import { translate } from "../account/identity-view-primitives.js";

const LIST_PATH = "/api/v1/admin/accounts";
const SEARCH_PATH = "/api/v1/admin/accounts/search";
const ACCESS_PATH = "/api/v1/admin/access";
const MAX_RESPONSE_BYTES = 128 * 1024;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const ACCOUNT_ID = /^acc_[0-9a-f]{32}$/;
const OPERATION_ID = /^op_[0-9a-f]{32}$/;
const COMMANDS = new Set([
  "grantAdministrator",
  "revokeAdministrator",
  "grantTrackerBeta",
  "revokeTrackerBeta",
  "grantContestModerator",
  "revokeContestModerator",
]);

function exactFields(value, required, optional = []) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const fields = Object.keys(value);
  return required.every((field) => fields.includes(field))
    && fields.every((field) => required.includes(field) || optional.includes(field));
}

function nullable(value, predicate) {
  return value === null || predicate(value);
}

export function decodeAdminAccountSummaryV1(value) {
  const fields = [
    "identityId", "accountId", "handle", "displayName", "maskedEmail",
    "identityState", "identityCreatedAt", "administrator", "trackerBeta", "contestModerator",
    "trackerNatsValidUntil",
  ];
  if (
    !exactFields(value, fields)
    || !UUID.test(value.identityId)
    || !nullable(value.accountId, (item) => ACCOUNT_ID.test(item))
    || !nullable(value.handle, (item) => typeof item === "string" && item.length > 0 && item.length <= 32)
    || !nullable(value.displayName, (item) => typeof item === "string" && item.length > 0 && item.length <= 80)
    || !nullable(value.maskedEmail, (item) => typeof item === "string" && item.length <= 320 && item.includes("@"))
    || !["active", "inactive"].includes(value.identityState)
    || typeof value.identityCreatedAt !== "string"
    || value.identityCreatedAt.length > 64
    || Number.isNaN(Date.parse(value.identityCreatedAt))
    || typeof value.administrator !== "boolean"
    || typeof value.trackerBeta !== "boolean"
    || typeof value.contestModerator !== "boolean"
    || !nullable(value.trackerNatsValidUntil, (item) => Number.isSafeInteger(item) && item > 0)
    || (value.accountId === null && (value.administrator || value.trackerBeta || value.contestModerator || value.trackerNatsValidUntil !== null))
  ) {
    throw new TypeError("Administrator account summary is invalid.");
  }
  return Object.freeze({ ...value });
}

export function decodeAdminAccountsPageV1(value) {
  if (
    !exactFields(value, ["schemaVersion", "accounts", "nextCursor"])
    || value.schemaVersion !== 1
    || !Array.isArray(value.accounts)
    || value.accounts.length > 50
    || !nullable(value.nextCursor, (item) => typeof item === "string" && item.length > 0 && item.length <= 1024)
  ) {
    throw new TypeError("Administrator account page is invalid.");
  }
  return Object.freeze({
    accounts: Object.freeze(value.accounts.map(decodeAdminAccountSummaryV1)),
    nextCursor: value.nextCursor,
  });
}

export function decodeAdminAccessMutationV1(value) {
  if (
    !exactFields(value, ["schemaVersion", "operationId", "applied", "account"])
    || value.schemaVersion !== 1
    || !OPERATION_ID.test(value.operationId)
    || typeof value.applied !== "boolean"
  ) {
    throw new TypeError("Administrator access result is invalid.");
  }
  return Object.freeze({
    operationId: value.operationId,
    applied: value.applied,
    account: decodeAdminAccountSummaryV1(value.account),
  });
}

function apiUrl(path) {
  const resolve = globalThis.window?.__fishystuffResolveApiUrl;
  if (typeof resolve !== "function") throw new TypeError("FishyStuff API URL resolver is unavailable.");
  return resolve(path);
}

function text(tag, value, className = "") {
  const node = document.createElement(tag);
  node.className = className;
  node.textContent = value;
  return node;
}

function expiryLabel(value) {
  return value === null ? "None" : new Date(value * 1000).toLocaleString();
}

export function isReauthenticationError(value) {
  return exactFields(value, ["error"])
    && exactFields(value.error, ["code", "message", "details"], ["request_id"])
    && value.error.code === "FORBIDDEN"
    && typeof value.error.message === "string"
    && value.error.message.length > 0
    && value.error.message.length <= 1024
    && (value.error.request_id === undefined || UUID.test(value.error.request_id))
    && exactFields(value.error.details, ["reason"])
    && value.error.details.reason === "reauthenticationRequired";
}

const HTMLElementBase = globalThis.HTMLElement ?? class {};

export class FishyAdminAccounts extends HTMLElementBase {
  #repository = null;
  #unsubscribe = null;
  #request = null;
  #accountId = null;
  #rows = [];
  #nextCursor = null;

  connectedCallback() {
    if (this.#unsubscribe) return;
    this.querySelector("[data-admin-search]")?.addEventListener("submit", this.#search);
    this.querySelector("[data-admin-next]")?.addEventListener("click", this.#next);
    this.querySelector("[data-admin-refresh]")?.addEventListener("click", this.#refresh);
    this.querySelector("[data-admin-show-directory]")?.addEventListener("click", this.#refresh);
    this.querySelector("[data-admin-accounts]")?.addEventListener("click", this.#mutate);
    this.#repository = browserAccountStateSessionRepository();
    this.#unsubscribe = this.#repository.subscribe(this.#sessionChanged);
    void this.#repository.load();
  }

  disconnectedCallback() {
    this.#request?.abort();
    this.#request = null;
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    this.querySelector("[data-admin-search]")?.removeEventListener("submit", this.#search);
    this.querySelector("[data-admin-next]")?.removeEventListener("click", this.#next);
    this.querySelector("[data-admin-refresh]")?.removeEventListener("click", this.#refresh);
    this.querySelector("[data-admin-show-directory]")?.removeEventListener("click", this.#refresh);
    this.querySelector("[data-admin-accounts]")?.removeEventListener("click", this.#mutate);
  }

  #sessionChanged = (state) => {
    if (["uninitialized", "loading"].includes(state.kind)) return this.#show("loading");
    if (state.kind === "signedOut") {
      this.#accountId = null;
      this.#cancel();
      return this.#show("signed-out");
    }
    if (state.kind === "unavailable") {
      this.#accountId = null;
      this.#cancel();
      return this.#show("unavailable");
    }
    if (state.kind === "ready" && state.accountId !== this.#accountId) {
      this.#accountId = state.accountId;
      void this.#loadPage(null, state.accountId);
    }
  };

  async #loadPage(cursor, accountId) {
    const request = this.#beginRequest();
    this.#show("loading");
    try {
      const suffix = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
      const response = await fetch(apiUrl(`${LIST_PATH}${suffix}`), {
        method: "GET", cache: "no-store", credentials: "include", redirect: "error",
        headers: { Accept: "application/json" }, signal: request.signal,
      });
      if (!this.#isCurrent(request, accountId)) return;
      if (response.status === 401) return this.#show("signed-out");
      if (response.status === 403) return this.#show("denied");
      if (!response.ok) throw new Error("Administrator account list failed.");
      const page = decodeAdminAccountsPageV1(
        await readBoundedAccountJson(response, "Administrator accounts", MAX_RESPONSE_BYTES),
      );
      if (!this.#isCurrent(request, accountId)) return;
      this.#rows = page.accounts;
      this.#nextCursor = page.nextCursor;
      this.#render();
      this.#show("ready");
    } catch (error) {
      if (!request.signal.aborted && error?.name !== "AbortError") this.#show("unavailable");
    } finally {
      if (this.#request === request) this.#request = null;
    }
  }

  #search = async (event) => {
    event.preventDefault();
    const session = this.#repository?.state();
    if (session?.kind !== "ready" || session.accountId !== this.#accountId) return;
    const form = new FormData(event.currentTarget);
    const kind = form.get("kind");
    const value = String(form.get("value") ?? "").trim();
    if (!["credentialIdentifier", "accountId", "kratosIdentity"].includes(kind) || !value) return;
    const selector = kind === "credentialIdentifier"
      ? { kind, identifier: value }
      : kind === "accountId" ? { kind, accountId: value } : { kind, identityId: value };
    const request = this.#beginRequest();
    this.#show("loading");
    try {
      const response = await fetch(apiUrl(SEARCH_PATH), {
        method: "POST", cache: "no-store", credentials: "include", redirect: "error",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ selector }), signal: request.signal,
      });
      if (!this.#isCurrent(request, session.accountId)) return;
      if (response.status === 401) return this.#show("signed-out");
      if (response.status === 403) return this.#show("denied");
      if (!response.ok) throw new Error("Administrator account search failed.");
      this.#rows = [decodeAdminAccountSummaryV1(
        await readBoundedAccountJson(response, "Administrator account search", MAX_RESPONSE_BYTES),
      )];
      this.#nextCursor = null;
      this.#render();
      const back = this.querySelector("[data-admin-show-directory]");
      if (back) back.hidden = false;
      this.#show("ready");
    } catch (error) {
      if (!request.signal.aborted && error?.name !== "AbortError") this.#show("unavailable");
    } finally {
      if (this.#request === request) this.#request = null;
    }
  };

  #next = () => {
    if (this.#nextCursor && this.#accountId) void this.#loadPage(this.#nextCursor, this.#accountId);
  };

  #refresh = () => {
    const back = this.querySelector("[data-admin-show-directory]");
    if (back) back.hidden = true;
    if (this.#accountId) void this.#loadPage(null, this.#accountId);
  };

  #mutate = async (event) => {
    const button = event.target?.closest?.("button[data-admin-command]");
    if (!button || button.disabled) return;
    const command = button.dataset.adminCommand;
    const identityId = button.dataset.adminIdentity;
    if (!COMMANDS.has(command) || !UUID.test(identityId ?? "")) return;
    const target = this.#rows.find((row) => row.identityId === identityId);
    if (!target) return;
    const credentialNote = target.trackerNatsValidUntil === null
      ? "No unexpired FishyStuff Desktop synchronization credential is recorded."
      : `The latest FishyStuff Desktop synchronization credential remains valid until ${expiryLabel(target.trackerNatsValidUntil)}.`;
    if (!globalThis.confirm(`Apply ${command} to ${target.handle ?? identityId}?\n\n${credentialNote}`)) return;

    const refreshed = await this.#repository.refresh();
    if (refreshed.kind !== "ready" || refreshed.accountId !== this.#accountId) return;
    const request = this.#beginRequest();
    this.#show("pending");
    try {
      const response = await fetch(apiUrl(ACCESS_PATH), {
        method: "POST", cache: "no-store", credentials: "include", redirect: "error",
        headers: accountMutationHeaders(refreshed),
        body: JSON.stringify({
          operationId: createAccountOperationId(),
          targetIdentityId: identityId,
          command,
        }),
        signal: request.signal,
      });
      if (!this.#isCurrent(request, refreshed.accountId)) return;
      if (response.status === 403) {
        const error = await readBoundedAccountJson(response, "Administrator access error", 8 * 1024);
        if (isReauthenticationError(error)) return this.#showReauthentication();
        return this.#show("denied");
      }
      if (response.status === 401) return this.#show("signed-out");
      if (!response.ok) throw new Error("Administrator access mutation failed.");
      const result = decodeAdminAccessMutationV1(
        await readBoundedAccountJson(response, "Administrator access result", MAX_RESPONSE_BYTES),
      );
      if (!this.#isCurrent(request, refreshed.accountId)) return;
      this.#result(
        `${result.applied ? "Access changed." : "Access was already in the requested state."} `
        + `Latest FishyStuff Desktop synchronization credential expiry: ${expiryLabel(result.account.trackerNatsValidUntil)}.`,
      );
      await this.#loadPage(null, refreshed.accountId);
    } catch (error) {
      if (!request.signal.aborted && error?.name !== "AbortError") this.#show("unavailable");
    } finally {
      if (this.#request === request) this.#request = null;
    }
  };

  #showReauthentication() {
    this.#show("reauthentication");
  }

  #render() {
    const body = this.querySelector("[data-admin-accounts]");
    if (!body) return;
    body.replaceChildren();
    for (const account of this.#rows) body.append(this.#row(account));
    const empty = this.querySelector("[data-admin-empty]");
    if (empty) empty.hidden = this.#rows.length !== 0;
    const next = this.querySelector("[data-admin-next]");
    if (next) next.hidden = this.#nextCursor === null;
  }

  #row(account) {
    const row = document.createElement("tr");
    const identity = document.createElement("td");
    identity.append(
      text("strong", account.displayName ?? account.handle ?? "Non-FishyStuff identity", "block"),
      text("code", account.identityId, "text-xs"),
      text("span", account.identityState, "block text-xs text-base-content/60"),
    );
    const mapped = text("td", account.accountId ?? "Unmapped", "font-mono text-xs");
    const email = text("td", account.maskedEmail ?? "Unavailable");
    const access = document.createElement("td");
    access.textContent = `${account.administrator ? "Administrator" : "Member"}; ${account.trackerBeta ? "Fishing Logger beta" : "no Fishing Logger beta"}`;
    access.append(text(
      "span",
      account.contestModerator
        ? translate("admin.contest_moderator", "Contest moderator")
        : translate("admin.no_contest_moderator", "No contest moderator role"),
      "block",
    ));
    const expiry = text("td", expiryLabel(account.trackerNatsValidUntil));
    const actions = document.createElement("td");
    actions.className = "flex min-w-52 flex-wrap justify-end gap-2";
    const grantable = account.identityState === "active" && account.handle !== null;
    const revocable = account.accountId !== null;
    const adminCommand = account.administrator ? "revokeAdministrator" : "grantAdministrator";
    const trackerCommand = account.trackerBeta ? "revokeTrackerBeta" : "grantTrackerBeta";
    const moderatorCommand = account.contestModerator ? "revokeContestModerator" : "grantContestModerator";
    actions.append(
      this.#action(
        account,
        adminCommand,
        account.administrator ? "Revoke admin" : "Grant admin",
        account.administrator ? revocable : grantable,
      ),
      this.#action(
        account,
        trackerCommand,
        account.trackerBeta ? "Revoke Fishing Logger" : "Grant Fishing Logger",
        account.trackerBeta ? revocable : grantable,
      ),
      this.#action(
        account,
        moderatorCommand,
        account.contestModerator
          ? translate("admin.revoke_contest_moderator", "Revoke contest moderator")
          : translate("admin.grant_contest_moderator", "Grant contest moderator"),
        account.contestModerator ? revocable : grantable,
      ),
    );
    row.append(identity, mapped, email, access, expiry, actions);
    return row;
  }

  #action(account, command, label, allowed) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn btn-sm btn-outline h-auto min-h-8 whitespace-normal";
    button.textContent = label;
    button.dataset.adminCommand = command;
    button.dataset.adminIdentity = account.identityId;
    button.disabled = !allowed
      || (command === "revokeAdministrator" && account.accountId === this.#accountId);
    return button;
  }

  #result(message) {
    const result = this.querySelector("[data-admin-result]");
    if (!result) return;
    result.textContent = message;
    result.hidden = false;
  }

  #beginRequest() {
    this.#cancel();
    const request = new AbortController();
    this.#request = request;
    return request;
  }

  #isCurrent(request, accountId) {
    return !request.signal.aborted && request === this.#request && accountId === this.#accountId;
  }

  #cancel() {
    this.#request?.abort();
    this.#request = null;
  }

  #show(state) {
    for (const element of this.querySelectorAll("[data-admin-state]")) {
      element.hidden = element.dataset.adminState !== state;
    }
  }
}

if (globalThis.customElements && !customElements.get("fishy-admin-accounts")) {
  customElements.define("fishy-admin-accounts", FishyAdminAccounts);
}
